import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Edit, Trash2, CheckCircle, XCircle, Download, Plus } from 'lucide-react';
import { brandsApi } from '../../lib/api';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import Modal from '../../components/Modal';

interface Brand {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
}

export default function BrandList() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['brands', search],
    queryFn: () => brandsApi.getAll({ search }),
  });

  const createMutation = useMutation({
    mutationFn: brandsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      toast.success('ブランドを登録しました');
      setIsModalOpen(false);
      resetForm();
    },
    onError: () => toast.error('登録に失敗しました'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => brandsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      toast.success('ブランドを更新しました');
      setIsModalOpen(false);
      resetForm();
    },
    onError: () => toast.error('更新に失敗しました'),
  });

  const deleteMutation = useMutation({
    mutationFn: brandsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      toast.success('ブランドを削除しました');
    },
    onError: () => toast.error('削除に失敗しました'),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map(id => brandsApi.delete(id)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      toast.success('選択したブランドを削除しました');
    },
    onError: () => toast.error('一括削除に失敗しました'),
  });

  const bulkStatusMutation = useMutation({
    mutationFn: async ({ ids, isActive }: { ids: string[]; isActive: boolean }) => {
      await Promise.all(ids.map(id => brandsApi.update(id, { isActive })));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      toast.success('ステータスを更新しました');
    },
    onError: () => toast.error('ステータス更新に失敗しました'),
  });

  const resetForm = () => {
    setFormData({ name: '', description: '' });
    setEditingBrand(null);
  };

  const handleEdit = (brand: Brand) => {
    setEditingBrand(brand);
    setFormData({
      name: brand.name,
      description: brand.description || '',
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingBrand) {
      updateMutation.mutate({ id: editingBrand.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('このブランドを削除しますか？')) {
      deleteMutation.mutate(id);
    }
  };

  const handleBulkDelete = (ids: string[]) => {
    if (confirm(`${ids.length}件のブランドを削除しますか？`)) {
      bulkDeleteMutation.mutate(ids);
    }
  };

  const handleBulkActivate = (ids: string[]) => {
    bulkStatusMutation.mutate({ ids, isActive: true });
  };

  const handleBulkDeactivate = (ids: string[]) => {
    bulkStatusMutation.mutate({ ids, isActive: false });
  };

  const handleExport = () => {
    const items = data?.data?.data || [];
    if (items.length === 0) {
      toast.error('エクスポートするデータがありません');
      return;
    }
    const headers = ['ブランド名', '説明', '状態'];
    const rows = items.map((item: Brand) => [
      item.name || '',
      item.description || '',
      item.isActive ? '有効' : '無効',
    ]);
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ブランドマスタ_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('CSVをエクスポートしました');
  };

  const columns = [
    { key: 'name', label: 'ブランド名', sortable: true },
    { key: 'description', label: '説明' },
    {
      key: 'isActive',
      label: '状態',
      width: '80px',
      sortable: true,
      render: (row: Brand) => (
        <span className={row.isActive ? 'badge badge-success' : 'badge badge-error'}>
          {row.isActive ? '有効' : '無効'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '操作',
      width: '100px',
      render: (row: Brand) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleEdit(row); }}
            className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: 'var(--accent-color)' }}
            title="編集"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(row.id); }}
            className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: 'var(--error-color)' }}
            title="削除"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ),
    },
  ];

  const bulkActions = [
    {
      label: '有効化',
      icon: <CheckCircle size={14} />,
      onClick: handleBulkActivate,
      variant: 'primary' as const,
    },
    {
      label: '無効化',
      icon: <XCircle size={14} />,
      onClick: handleBulkDeactivate,
      variant: 'outline' as const,
    },
    {
      label: '一括削除',
      icon: <Trash2 size={14} />,
      onClick: handleBulkDelete,
      variant: 'danger' as const,
    },
  ];

  return (
    <div className="animate-fadeIn">
      <PageHeader
        title="ブランドマスタ"
        breadcrumbs={[{ label: 'マスタ管理' }]}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="ブランド名で検索..."
        onApply={() => refetch()}
        actions={
          <div className="flex items-center gap-1.5">
            <button className="btn btn-outline btn-sm" onClick={handleExport}>
              <Download size={14} />
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => { resetForm(); setIsModalOpen(true); }}>
              <Plus size={14} />
              ブランド登録
            </button>
          </div>
        }
      />

      <DataTable
        columns={columns}
        data={data?.data?.data || []}
        loading={isLoading}
        selectable
        bulkActions={bulkActions}
        getRowId={(row: Brand) => row.id}
        emptyMessage="ブランドが登録されていません"
        defaultSortKey="name"
        defaultSortOrder="asc"
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); resetForm(); }}
        title={editingBrand ? 'ブランド編集' : 'ブランド登録'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">ブランド名 <span className="text-red-500">*</span></label>
            <input
              type="text"
              className="input-field"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="label">説明</label>
            <textarea
              className="input-field"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => { setIsModalOpen(false); resetForm(); }}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingBrand ? '更新' : '登録'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
